import { formatDateForCms1500 } from './cms1500Utils';
import { claimTotal, type ClaimLineItem, type ClaimRow } from './mockClaims';

export const CMS1500_DIAGNOSIS_SLOTS = 12;
export const CMS1500_SERVICE_LINE_SLOTS = 6;

export interface Cms1500ServiceLineForm {
  dateFrom: string;
  dateTo: string;
  pos: string;
  emg: string;
  cpt: string;
  modifier: string;
  dxPointer: string;
  charges: string;
  units: string;
  epsdt: string;
  qual: string;
  renderingId: string;
}

export interface Cms1500FormState {
  payer: string;
  payerId: string;
  subscriberId: string;
  patientName: string;
  insuredName: string;
  patientAddress: string;
  patientCityStateZip: string;
  insuredAddress: string;
  insuredCityStateZip: string;
  insurancePlanName: string;
  illnessDate: string;
  otherDate: string;
  unableToWorkDates: string;
  referringProvider: string;
  hospitalizationDates: string;
  additionalInfo: string;
  diagnoses: string[];
  resubmissionCode: string;
  originalRefNo: string;
  priorAuth: string;
  taxId: string;
  patientAccountNo: string;
  totalCharge: string;
  amountPaid: string;
  renderingProvider: string;
  serviceFacility: string;
  billingProvider: string;
  billingNpi: string;
  serviceLines: Cms1500ServiceLineForm[];
}

function splitName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[parts.length - 1]}, ${parts[0]} ${parts.slice(1, -1).join(' ')}`.trim();
}

function emptyServiceLine(): Cms1500ServiceLineForm {
  return {
    dateFrom: '',
    dateTo: '',
    pos: '',
    emg: '',
    cpt: '',
    modifier: '',
    dxPointer: '',
    charges: '',
    units: '',
    epsdt: '',
    qual: '',
    renderingId: '',
  };
}

function serviceLineFromItem(serviceDate: string, item: ClaimLineItem | null): Cms1500ServiceLineForm {
  if (!item) return emptyServiceLine();
  const date = formatDateForCms1500(serviceDate);
  return {
    dateFrom: date,
    dateTo: date,
    pos: '11',
    emg: '',
    cpt: item.code,
    modifier: '',
    dxPointer: 'A',
    charges: item.net.toFixed(2),
    units: String(item.quantity),
    epsdt: '',
    qual: '',
    renderingId: '',
  };
}

function defaultResubmissionCode(claim: ClaimRow): string {
  if (claim.rcmStatus === 'rejected' || claim.rcmStatus === 'denied') return '7';
  return '';
}

export function buildCms1500FormState(claim: ClaimRow): Cms1500FormState {
  const patientName = splitName(claim.patientName);
  const allLines = claim.encounters.flatMap((enc) =>
    enc.items.map((item) => ({ serviceDate: enc.serviceDate, item })),
  );
  const diagnoses = Array.from({ length: CMS1500_DIAGNOSIS_SLOTS }, (_, index) => claim.diagnosisCodes?.[index] ?? '');
  const serviceLines = Array.from({ length: CMS1500_SERVICE_LINE_SLOTS }, (_, index) => {
    const line = allLines[index];
    return line ? serviceLineFromItem(line.serviceDate, line.item) : emptyServiceLine();
  });

  return {
    payer: claim.payer,
    payerId: '',
    subscriberId: claim.subscriberId ?? '',
    patientName,
    insuredName: patientName,
    patientAddress: '',
    patientCityStateZip: '',
    insuredAddress: '',
    insuredCityStateZip: '',
    insurancePlanName: claim.payer,
    illnessDate: formatDateForCms1500(allLines[0]?.serviceDate ?? ''),
    otherDate: '',
    unableToWorkDates: '',
    referringProvider: '',
    hospitalizationDates: '',
    additionalInfo: '',
    diagnoses,
    resubmissionCode: defaultResubmissionCode(claim),
    originalRefNo: claim.payerClaimNumber ?? '',
    priorAuth: '',
    taxId: '',
    patientAccountNo: claim.accountId ?? '',
    totalCharge: claimTotal(claim).toFixed(2),
    amountPaid: '',
    renderingProvider: claim.provider,
    serviceFacility: '',
    billingProvider: 'Meridian Counseling',
    billingNpi: '',
    serviceLines,
  };
}
